import { useCallback, useRef } from 'react';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { getSanitisedUrl } from '@lyearn/core/utils/url';
import { useSnackbar } from '@lyearn/ui';
import useLinking from '@/hooks/useLinking';
import useNavigation from '@/hooks/useNavigation';
import config from '@/modules/content/config';
import { appendParentEntityId } from '@/modules/corporate/modules/exercise/views/Review/utils';
// import { useCreateMgsaMutation } from '@/modules/productivity/views/builder-new/graphql/mutations/generated/createMgsa';
import { adaptEnumValue } from '@/modules/reporting/utils/adapt';
import routes from '@/pages/corporate/routes';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { EntityTypeEnum, ResourceTypeEnum } from '@/types/schema';

import { ReportTableAction, ReportTableActionTypes } from './actions';

export const EVENT_CONTENT_TYPE: Record<string, ResourceTypeEnum> = {
  LIVE: ResourceTypeEnum.LiveEventContent,
  OFFLINE: ResourceTypeEnum.OfflineEventContent,
  EXTERNAL: ResourceTypeEnum.ExternalEventContent,
};

const useReportTableActions = () => {
  const { openLink } = useLinking();
  const isAdminRoute = useAdminRoute();
  const navigation = useNavigation();
  // const [{ fetching }, createMgsa] = useCreateMgsaMutation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const creatingMGSARef = useRef<boolean>(false);

  const onAction = useCallback(
    async (action: ReportTableAction) => {
      const { row } = action.payload;

      switch (action.type) {
        case ReportTableActionTypes.GoToContent: {
          let contentType = adaptEnumValue(row.entity_type) as ResourceTypeEnum;
          const entityType = adaptEnumValue(row.entityType) as EntityTypeEnum;

          if (entityType === EntityTypeEnum.Event) {
            contentType = EVENT_CONTENT_TYPE[row.entity_type.toUpperCase()];
          }

          const queryParams = appendParentEntityId(row.parent_entity_id);

          isAdminRoute
            ? openLink(config[contentType].routes.manageDetails(row.id) + queryParams)
            : openLink(config[contentType].routes.details(row.id) + queryParams);
          break;
        }

        case ReportTableActionTypes.GoToContentUrl: {
          openLink(getSanitisedUrl(row.entity_content_url));
          break;
        }

        case ReportTableActionTypes.GoToSession: {
          isAdminRoute
            ? openLink(routes.AdminEventDetails(row.id))
            : openLink(routes.EventDetails(row.id));
          break;
        }

        // todo- remove this in the future. "ViewProfile" and "ViewUserProfile" are the same.
        case ReportTableActionTypes.ViewProfile: {
          const profileRoute = isAdminRoute
            ? routes.AdminUserProfile(row.id)
            : routes.UserProfile(row.id);
          navigation.push(profileRoute, { appendQuery: false });
          break;
        }

        case ReportTableActionTypes.ViewUserProfile: {
          const profileRoute = isAdminRoute
            ? routes.AdminUserProfile(row.id)
            : routes.UserProfile(row.id);
          navigation.push(profileRoute, { appendQuery: false });
          break;
        }

        case ReportTableActionTypes.ViewMgsa: {
          navigation.push(routes.ViewUserMgsa(row.id), {
            appendQuery: false,
          });
          break;
        }

        case ReportTableActionTypes.EditMgsa: {
          navigation.push(routes.EditUserMgsa(row.id, row.orgRole.id, row.department.id), {
            appendQuery: false,
          });
          break;
        }
        case ReportTableActionTypes.CreateMgsa: {
          if (creatingMGSARef.current) return;

          const userId = row.id;
          const roleId = row.orgRole?.id;
          const departmentId = row.department?.id;
          const handleClick = async () => {
            creatingMGSARef.current = true;
            showSnackbar({
              message: 'Creating MGSA...',
              position: { vertical: 'bottom', horizontal: 'left' },
            });
            try {
              // await createMgsa({
              //   data: {
              //     entityId: roleId,
              //     entityType: MgsaEntityTypeEnum.OrgRole,
              //   },
              // });
              ErrorReporter.error(
                new Error('MGSA Creation API has been changed, Please update to the new API'),
              );
            } catch (err) {
              showErrorSnackbar({
                message: 'Failed to create MGSA. Please try again.',
                position: { vertical: 'bottom', horizontal: 'left' },
              });
              creatingMGSARef.current = false;
              return;
            }
            showSnackbar({
              message: 'MGSA created successfully',
              position: { vertical: 'bottom', horizontal: 'left' },
            });
            creatingMGSARef.current = false;
            navigation.push(routes.CreateUserMgsa(userId, roleId, departmentId), {
              appendQuery: false,
            });
          };
          await handleClick();

          break;
        }
      }
    },
    [isAdminRoute, openLink, showSnackbar, navigation, showErrorSnackbar],
  );

  return {
    onAction,
    fetching: false,
  };
};

export default useReportTableActions;
